import { Plus } from 'lucide-react'
import * as React from 'react'

import { DatePicker } from '@/components/date-picker'
import { DecisionDue, DecisionTipoBadge } from '@/components/domain'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { createDecision, updateTask } from '@/data/api'
import type { Decision, Task } from '@/data/types'
import { invalidate } from '@/data/use-load'
import { useMemberName, useSession } from '@/lib/session'
import { useWrite } from '@/lib/use-write'
import { DecisionActions, DecisionStatusSelect } from '@/modules/decisions/decision-parts'

import { MemberSelect } from './fields'

/**
 * Q3 da tarefa: Que bom · Que pena · Que tal? (decisões). As decisões têm
 * dono, prazo e status, e podem virar tarefa ou ação para não morrerem na ata.
 */
export function Q3Panel({
  task,
  decisions,
  canEditQ3,
  canEditDecisions,
}: {
  task: Task
  decisions: Decision[]
  canEditQ3: boolean
  canEditDecisions: boolean
}) {
  const { run, pending } = useWrite()
  const [bom, setBom] = React.useState(task.q3_bom ?? '')
  const [pena, setPena] = React.useState(task.q3_pena ?? '')
  React.useEffect(() => {
    setBom(task.q3_bom ?? '')
    setPena(task.q3_pena ?? '')
  }, [task.q3_bom, task.q3_pena])

  async function save(field: 'q3_bom' | 'q3_pena', value: string) {
    if ((task[field] ?? '') === value.trim()) return
    const r = await run(updateTask(task.id, { [field]: value.trim() || null }), { success: 'Q3 salva' })
    if (r.ok) invalidate()
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1.5 border-l-2 border-success pl-3">
        <Label htmlFor="q3-bom" className="text-[11px] uppercase tracking-wider">
          Que bom · o que funcionou e deve ser mantido
        </Label>
        <Textarea
          id="q3-bom"
          rows={3}
          value={bom}
          disabled={!canEditQ3 || pending}
          onChange={(e) => setBom(e.target.value)}
          onBlur={() => void save('q3_bom', bom)}
        />
      </div>
      <div className="space-y-1.5 border-l-2 border-warning pl-3">
        <Label htmlFor="q3-pena" className="text-[11px] uppercase tracking-wider">
          Que pena · o que não funcionou, dificuldades, desvios
        </Label>
        <Textarea
          id="q3-pena"
          rows={3}
          value={pena}
          disabled={!canEditQ3 || pending}
          onChange={(e) => setPena(e.target.value)}
          onBlur={() => void save('q3_pena', pena)}
        />
      </div>
      <div className="space-y-2 border-l-2 border-primary pl-3">
        <div className="text-[11px] font-medium uppercase tracking-wider">Que tal? · decisões tomadas</div>
        <DecisionList decisions={decisions} canEdit={canEditDecisions} />
        {canEditDecisions && <NewDecision taskId={task.id} areaId={task.area_id} />}
      </div>
    </div>
  )
}

export function DecisionList({
  decisions,
  canEdit,
}: {
  decisions: Decision[]
  /** Booleano para a lista toda, ou por decisão (a permissão é da área da decisão). */
  canEdit: boolean | ((d: Decision) => boolean)
}) {
  const editable = (d: Decision) => (typeof canEdit === 'function' ? canEdit(d) : canEdit)
  const name = useMemberName()

  if (!decisions.length) return <p className="text-sm text-muted-foreground">Nenhuma decisão registrada.</p>
  return (
    <ul className="divide-y divide-border rounded-md border border-border">
      {decisions.map((d) => (
        <li key={d.id} className="flex items-start gap-2 px-3 py-2.5">
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-sm leading-snug">{d.decisao}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <DecisionStatusSelect decision={d} canEdit={editable(d)} />
              <DecisionTipoBadge tipo={d.tipo} />
              {d.tipo === 'executar' && (
                <span className="text-xs text-muted-foreground">{d.owner_id ? name(d.owner_id) : (d.owner_text ?? 'Sem responsável')}</span>
              )}
              {d.prazo && <DecisionDue prazo={d.prazo} status={d.status} />}
            </div>
          </div>
          <DecisionActions decision={d} canEdit={editable(d)} />
        </li>
      ))}
    </ul>
  )
}

function NewDecision({ taskId, areaId }: { taskId: string; areaId: string }) {
  const { ref } = useSession()
  const { run, pending } = useWrite()
  const [v, setV] = React.useState<{ decisao: string; owner_id: string | null; prazo: string }>({ decisao: '', owner_id: null, prazo: '' })
  const members = React.useMemo(() => {
    const inArea = new Set(ref.areaMembers.filter((am) => am.area_id === areaId).map((am) => am.member_id))
    return [...ref.members.filter((m) => m.is_active)].sort((a, b) => Number(inArea.has(b.id)) - Number(inArea.has(a.id)))
  }, [ref, areaId])

  return (
    <form
      className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_170px_150px_auto]"
      onSubmit={(e) => {
        e.preventDefault()
        if (!v.decisao.trim()) return
        void run(createDecision({ task_id: taskId, decisao: v.decisao.trim(), owner_id: v.owner_id, prazo: v.prazo || null })).then((r) => {
          if (r.ok) {
            setV({ decisao: '', owner_id: null, prazo: '' })
            invalidate()
          }
        })
      }}
    >
      <Input className="col-span-2 sm:col-span-1" aria-label="Nova decisão" placeholder="Decisão" value={v.decisao} onChange={(e) => setV({ ...v, decisao: e.target.value })} />
      <MemberSelect value={v.owner_id} onChange={(id) => setV({ ...v, owner_id: id })} members={members} placeholder="Responsável" />
      <DatePicker aria-label="Prazo da decisão" placeholder="Prazo" value={v.prazo} onChange={(prazo) => setV({ ...v, prazo })} />
      <Button type="submit" variant="outline" className="col-span-2 sm:col-span-1" disabled={pending || !v.decisao.trim()}>
        <Plus /> Decisão
      </Button>
    </form>
  )
}
